import { useEffect, useState } from 'react';
import {
  collection, query, where, onSnapshot,
  updateDoc, deleteDoc, doc, serverTimestamp, getDocs, writeBatch, addDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { downloadInvoicePdf } from '../utils/downloadInvoicePdf';
import { downloadFinanceSummaryPdf } from '../utils/downloadFinanceSummaryPdf';
import { getCurrentWeekRates, toGBP, getRateForCurrency } from '../utils/exchangeRates';
import { sendConfirmationLetterEmail } from '../utils/sendConfirmationLetterEmail';
import { usesParentLetters } from '../utils/letterAssets';
import ConfirmDialog from './ConfirmDialog';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function fmt(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

async function generateInvoiceNumber(brand) {
  const year = new Date().getFullYear();
  const snap = await getDocs(collection(db, 'invoices'));
  const seq = String(snap.size + 1).padStart(3, '0');
  const prefix = brand === 'your_tour' ? 'YT' : brand === 'padelante' ? 'PAD' : 'MST';
  return `${prefix}-${year}-${seq}`;
}

const BLANK_INSTALMENT = { label: '', amount: '', dueDate: '' };

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function midDate(dateA, dateB) {
  if (!dateA || !dateB) return '';
  const a = new Date(dateA).getTime();
  const b = new Date(dateB).getTime();
  return new Date(Math.round((a + b) / 2)).toISOString().slice(0, 10);
}

const DEFAULT_BANK_DETAILS =
`Recipient: MST SPORTS TOURS LTD
Address: 48 - 52 Penny Lane Mossley Hill, L18 1DG, Liverpool, United Kingdom

Payment from a UK bank
Account number: 71581634
Sort code: 04-29-09

Payment from outside the UK
IBAN: GB75 REVO 0099 6973 9386 09
BIC: REVOGB21
Intermediary BIC: CHASGB2L`;

export default function InvoicePanel({ requestId, request, brand }) {
  const isYourTour = brand === 'your_tour';
  // Spain → UK tours watch the Premier League, not La Liga.
  const competition = request?.market === 'spain_to_uk' ? 'Premier League' : 'La Liga';
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [instalments, setInstalments] = useState([{ ...BLANK_INSTALMENT, label: 'Deposit' }]);
  const [bankDetails, setBankDetails] = useState(DEFAULT_BANK_DETAILS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Match ticket state
  const [mtStatus, setMtStatus] = useState('pending');
  const [mtHomeTeam, setMtHomeTeam] = useState('Elche');
  const [mtFixtureDate, setMtFixtureDate] = useState('');
  const [mtKickOffTime, setMtKickOffTime] = useState('');
  const [mtCostToMST, setMtCostToMST] = useState('');
  const [mtChargeToCustomer, setMtChargeToCustomer] = useState('');
  const [mtChargeManuallySet, setMtChargeManuallySet] = useState(false);
  const [mtNumberOfTickets, setMtNumberOfTickets] = useState('');
  const [savingMt, setSavingMt] = useState(false);
  const [mtSaved, setMtSaved] = useState(false);
  const [generatingMtInvoice, setGeneratingMtInvoice] = useState(false);
  const [mtInvoiceError, setMtInvoiceError] = useState('');
  const [exchangeRates, setExchangeRates] = useState(null);

  const sym = CURRENCY_SYMBOLS[request?.currency] || '£';
  const totalQuoted = request?.quoteDetails?.totalCostToCustomer ?? null;
  const hasMatchTickets = (request?.selectedAddons || []).some((a) => a.categoryName === 'Match Tickets');

  // Everyone travelling needs a ticket — players, supporters and the customer's
  // own staff/teachers — plus the MST staff on the trip.
  const defaultTickets = (Number(request?.participants) || 0)
    + (Number(request?.supportersMale) || 0) + (Number(request?.supportersFemale) || 0)
    + (Number(request?.customerStaff) || 0) + (Number(request?.staffCount) || 1);
  const payingPlayers = request?.quoteDetails?.payingPlayers ?? (Number(request?.participants) || 1);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), where('requestId', '==', requestId));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.instalmentNumber ?? 0) - (b.instalmentNumber ?? 0) ||
          (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
      setInvoices(docs);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  useEffect(() => {
    getCurrentWeekRates().then(setExchangeRates).catch(console.error);
  }, []);

  // Sync match ticket form fields from Firestore whenever request.matchTicket changes
  useEffect(() => {
    if (!request?.matchTicket) return;
    const mt = request.matchTicket;
    setMtStatus(mt.status || 'pending');
    setMtHomeTeam(mt.homeTeam || 'Elche');
    setMtFixtureDate(mt.fixtureDate || '');
    setMtKickOffTime(mt.kickOffTime || '');
    setMtCostToMST(mt.costToMSTPerTicketEUR != null ? String(mt.costToMSTPerTicketEUR) : '');
    if (mt.chargeToCustomerPerTicket != null) {
      setMtChargeToCustomer(String(mt.chargeToCustomerPerTicket));
      setMtChargeManuallySet(true);
    }
    setMtNumberOfTickets(mt.numberOfTickets != null ? String(mt.numberOfTickets) : '');
  }, [request?.matchTicket]);

  // Auto-calculate customer charge per ticket from MST cost + exchange rates
  useEffect(() => {
    if (mtChargeManuallySet || !mtCostToMST || !exchangeRates) return;
    const ticketCount = Number(mtNumberOfTickets) || defaultTickets;
    const totalEUR = ticketCount * Number(mtCostToMST);
    const totalGBP = toGBP(totalEUR, 'EUR', exchangeRates);
    const pp = payingPlayers || 1;
    setMtChargeToCustomer((totalGBP / pp).toFixed(2));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mtCostToMST, mtNumberOfTickets, exchangeRates, mtChargeManuallySet]);

  function applyDefaultPlan() {
    // £100 deposit per paying traveller — the same figure the quote letter's
    // payment schedule uses, so the two never disagree.
    const depositHeads = Number(payingPlayers) || 0;
    const depositAmt  = depositHeads > 0 ? depositHeads * 100 : 100;
    const total       = totalQuoted ?? 0;
    const remaining   = Math.max(0, total - depositAmt);
    const instalment1 = Math.round(remaining * 0.5 * 100) / 100;
    const instalment2 = Math.round((remaining - instalment1) * 100) / 100;

    const finalDate      = addMonths(request?.departureDate, -2);
    const depositDate    = new Date();
    depositDate.setDate(depositDate.getDate() + 14);
    const depositDateStr = depositDate.toISOString().slice(0, 10);
    const mid1Date       = midDate(depositDateStr, finalDate);

    setInstalments([
      { label: 'Deposit',          amount: String(depositAmt),  dueDate: depositDateStr },
      { label: '2nd Instalment',   amount: String(instalment1), dueDate: mid1Date       },
      { label: 'Final Instalment', amount: String(instalment2), dueDate: finalDate      },
    ]);
  }

  function addInstalment() {
    const num = instalments.length + 1;
    setInstalments((prev) => [
      ...prev,
      { ...BLANK_INSTALMENT, label: num === 2 ? 'First Instalment' : `Instalment ${num - 1}` },
    ]);
  }

  function removeInstalment(idx) {
    setInstalments((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateInstalment(idx, field, value) {
    setInstalments((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }

  const instalmentTotal = instalments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0);
  const remaining = totalQuoted != null ? totalQuoted - instalmentTotal : null;

  async function handleGenerateInvoices(e) {
    e.preventDefault();
    setError('');

    for (const [idx, inst] of instalments.entries()) {
      if (!inst.label.trim()) { setError(`Instalment ${idx + 1} needs a label.`); return; }
      if (!inst.amount || parseFloat(inst.amount) <= 0) { setError(`Instalment ${idx + 1} needs a valid amount.`); return; }
      if (!inst.dueDate) { setError(`Instalment ${idx + 1} needs a due date.`); return; }
    }
    if (!bankDetails.trim()) { setError('Bank details are required.'); return; }

    setSaving(true);
    try {
      const rates = await getCurrentWeekRates();
      const currency = request?.currency || 'GBP';
      const batch = writeBatch(db);
      const total = instalments.length;
      const invoiceRefs = [];
      for (let i = 0; i < instalments.length; i++) {
        const inst = instalments[i];
        const invoiceNumber = await generateInvoiceNumber(request?.brand);
        const ref = doc(collection(db, 'invoices'));
        invoiceRefs.push(ref);
        const amount = parseFloat(inst.amount);
        batch.set(ref, {
          requestId,
          invoiceNumber,
          customerEmail: request?.customerEmail || '',
          customerId: request?.customerId || '',
          requestSummary: request ? `${request.sport} — ${request.destination}` : '',
          instalmentLabel: inst.label.trim(),
          instalmentNumber: i + 1,
          totalInstalments: total,
          amount,
          currency,
          dueDate: inst.dueDate,
          bankDetails: bankDetails.trim(),
          brand: request?.brand || 'mst',
          market: request?.market || 'uk_to_spain',
          status: 'unpaid',
          paidAt: null,
          markedPaidBy: null,
          gbpEquivalent: toGBP(amount, currency, rates),
          exchangeRateUsed: getRateForCurrency(currency, rates),
          rateWeek: rates.weekStart,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }
      await batch.commit();

      if (isYourTour) {
        await addDoc(collection(db, 'yourTourFinanceRequests'), {
          requestId,
          invoiceIds: invoiceRefs.map((r) => r.id),
          createdAt: serverTimestamp(),
          status: 'pending',
        });
      }

      setShowPlanForm(false);
      setInstalments([{ ...BLANK_INSTALMENT, label: 'Deposit' }]);
      setBankDetails(DEFAULT_BANK_DETAILS);
    } catch (err) {
      setError('Failed to generate invoices. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function saveMatchTicket() {
    setSavingMt(true);
    setMtSaved(false);
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        matchTicket: {
          ...(request?.matchTicket || {}),
          status: mtStatus,
          homeTeam: mtHomeTeam,
          fixtureDate: mtFixtureDate,
          kickOffTime: mtKickOffTime,
          costToMSTPerTicketEUR: Number(mtCostToMST) || 0,
          chargeToCustomerPerTicket: Number(mtChargeToCustomer) || 0,
          numberOfTickets: Number(mtNumberOfTickets) || defaultTickets,
        },
      });
      setMtSaved(true);
      setTimeout(() => setMtSaved(false), 3000);
    } finally {
      setSavingMt(false);
    }
  }

  async function generateMatchTicketInvoice() {
    setGeneratingMtInvoice(true);
    setMtInvoiceError('');
    const mt = request?.matchTicket;
    const numberOfTickets = Number(mt?.numberOfTickets) || defaultTickets;
    const chargePerTicket = Number(mt?.chargeToCustomerPerTicket) || 0;
    const totalAmount = numberOfTickets * chargePerTicket;
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      const dueDateStr = dueDate.toISOString().slice(0, 10);
      const invoiceNumber = await generateInvoiceNumber(request?.brand);
      const invoiceRef = await addDoc(collection(db, 'invoices'), {
        invoiceType: 'match_ticket',
        requestId,
        requestSummary: request?.tourName || `${request?.sport} — ${request?.destination}`,
        customerEmail: request?.customerEmail || '',
        brand: request?.brand || 'mst',
        market: request?.market || 'uk_to_spain',
        currency: request?.currency || 'GBP',
        amount: totalAmount,
        status: 'unpaid',
        dueDate: dueDateStr,
        invoiceNumber,
        instalmentLabel: `${competition} Match Tickets`,
        instalmentNumber: 1,
        totalInstalments: 1,
        bankDetails: DEFAULT_BANK_DETAILS,
        matchTicketDetails: {
          homeTeam: mt?.homeTeam || '',
          fixtureDate: mt?.fixtureDate || '',
          kickOffTime: mt?.kickOffTime || '',
          numberOfTickets,
          chargeToCustomerPerTicket: chargePerTicket,
        },
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'requests', requestId), {
        'matchTicket.invoiceId': invoiceRef.id,
      });
      if (isYourTour) {
        await addDoc(collection(db, 'yourTourFinanceRequests'), {
          requestId,
          invoiceIds: [invoiceRef.id],
          createdAt: serverTimestamp(),
          status: 'pending',
        });
      }
    } catch (e) {
      setMtInvoiceError('Failed to generate invoice. Please try again.');
      console.error(e);
    } finally {
      setGeneratingMtInvoice(false);
    }
  }

  async function markPaid(invoiceId) {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (inv?.status === 'paid') return;
    await updateDoc(doc(db, 'invoices', invoiceId), {
      status: 'paid',
      paidAt: serverTimestamp(),
      markedPaidBy: currentUser.email,
    });
    // The confirmation letter fires off the deposit specifically, not just
    // any instalment — mirrors the same check InvoicePanel already relies on
    // via applyDefaultPlan's numbering.
    if (inv && inv.instalmentNumber === 1 && inv.invoiceType !== 'match_ticket' && usesParentLetters(request)) {
      try { await sendConfirmationLetterEmail(request); }
      catch (e) { console.error('Confirmation letter send failed:', e); }
    }
  }

  async function markUnpaid(invoiceId) {
    await updateDoc(doc(db, 'invoices', invoiceId), {
      status: 'unpaid',
      paidAt: null,
      markedPaidBy: null,
    });
  }

  async function deleteInvoice(invoiceId) {
    await deleteDoc(doc(db, 'invoices', invoiceId));
    setConfirmDelete(null);
  }

  // Separate match ticket invoice from regular instalments
  const regularInvoices = invoices.filter((i) => i.invoiceType !== 'match_ticket');
  const matchTicketInvoice = invoices.find((i) => i.invoiceType === 'match_ticket');

  const paidTotal   = regularInvoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const unpaidTotal = regularInvoices.filter((i) => i.status === 'unpaid').reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const invoiceAlreadyGenerated = !!request?.matchTicket?.invoiceId || !!matchTicketInvoice;

  function InvoiceRow({ inv }) {
    const invSym = CURRENCY_SYMBOLS[inv.currency] || '£';
    return (
      <div className="border border-[#E5E7EB] rounded-[8px] p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[13px] font-semibold text-[#111827]">{inv.invoiceNumber}</span>
              {inv.instalmentLabel && (
                <span className="text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] bg-[#F3F4F6] text-[#6B7280]">
                  {inv.instalmentLabel}
                  {inv.totalInstalments > 1 && ` (${inv.instalmentNumber}/${inv.totalInstalments})`}
                </span>
              )}
              <span className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${
                inv.status === 'paid' ? 'bg-green-light text-[#065F46]' : 'bg-amber-light text-[#92400E]'
              }`}>
                {inv.status === 'paid' ? 'Paid' : 'Unpaid'}
              </span>
            </div>
            <p className="text-[12px] text-[#9CA3AF]">
              Due {formatDate(inv.dueDate)}
              {inv.status === 'paid' && inv.markedPaidBy && ` · Marked paid by ${inv.markedPaidBy}`}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[15px] font-bold text-[#111827]">{fmt(inv.amount, invSym)}</p>
            <div className="flex items-center justify-end gap-3 mt-1">
              {!isYourTour && (
                <button
                  onClick={async () => {
                    setDownloadingId(inv.id);
                    try { await downloadInvoicePdf(inv.id, inv.invoiceNumber); }
                    catch { alert('Failed to download PDF. Please try again.'); }
                    finally { setDownloadingId(null); }
                  }}
                  disabled={downloadingId === inv.id}
                  className="text-[12px] text-pb hover:text-navy font-medium disabled:opacity-50"
                >
                  {downloadingId === inv.id ? 'Generating…' : 'PDF'}
                </button>
              )}
              {!isYourTour && (inv.status === 'unpaid' ? (
                <button onClick={() => markPaid(inv.id)}
                  className="text-[12px] text-sg hover:text-[#155e37] font-medium">
                  Mark as paid
                </button>
              ) : (
                <button onClick={() => markUnpaid(inv.id)}
                  className="text-[12px] text-[#9CA3AF] hover:text-[#6B7280]">
                  Mark as unpaid
                </button>
              ))}
              <button
                onClick={() => setConfirmDelete({ id: inv.id, number: inv.invoiceNumber })}
                className="text-[12px] text-dr/60 hover:text-dr font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        {inv.bankDetails && (
          <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Bank Details</p>
            <p className="text-[12px] text-[#6B7280] whitespace-pre-line">{inv.bankDetails}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete invoice ${confirmDelete?.number}?`}
        message="This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={() => deleteInvoice(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Invoices</h3>
            {isYourTour && (
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-[5px] bg-pb text-white">Your Tour</span>
            )}
          </div>
          {isYourTour && (
            <p className="text-[12px] text-pb mt-0.5">Invoices are raised for the Your Tour finance team to send.</p>
          )}
          {!isYourTour && totalQuoted != null && (
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">
              Quoted total: <span className="font-medium text-[#374151]">{fmt(totalQuoted, sym)}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isYourTour && regularInvoices.length > 0 && (
            <button
              onClick={() => downloadFinanceSummaryPdf(request, regularInvoices, matchTicketInvoice)}
              className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition"
            >
              Download Finance Summary
            </button>
          )}
          {!showPlanForm && (
            <button
              onClick={() => { setShowPlanForm(true); setError(''); }}
              className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition"
            >
              + Create Instalment Plan
            </button>
          )}
        </div>
      </div>

      {/* Instalment plan form */}
      {showPlanForm && (
        <form onSubmit={handleGenerateInvoices} className="mb-6 space-y-5 bg-surface rounded-[10px] p-5 border border-[#E5E7EB]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[13px] font-semibold text-[#374151] mb-1">Instalment Plan</p>
              <p className="text-[12px] text-[#9CA3AF]">
                Define each payment instalment. Once saved, individual invoices will be generated automatically.
              </p>
            </div>
            <button
              type="button"
              onClick={applyDefaultPlan}
              title="Deposit = £100 × participants. Final payment = 2 months before departure. 1st instalment = midpoint, 50% of remaining."
              className="flex-shrink-0 text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition whitespace-nowrap"
            >
              Use default plan
            </button>
          </div>

          {totalQuoted != null && (
            <div className={`flex items-center justify-between text-[12px] px-3 py-2 rounded-[8px] border ${
              Math.abs(remaining) < 0.01
                ? 'bg-green-light border-sg/20 text-[#065F46]'
                : remaining > 0
                ? 'bg-amber-light border-ba/25 text-[#92400E]'
                : 'bg-red-light border-dr/20 text-dr'
            }`}>
              <span>Quoted total: <strong>{fmt(totalQuoted, sym)}</strong></span>
              <span>Instalments total: <strong>{fmt(instalmentTotal, sym)}</strong></span>
              <span>
                {Math.abs(remaining) < 0.01
                  ? '✓ Balanced'
                  : remaining > 0
                  ? `${fmt(remaining, sym)} remaining`
                  : `${fmt(Math.abs(remaining), sym)} over-allocated`}
              </span>
            </div>
          )}

          <div className="space-y-3">
            {instalments.map((inst, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  {idx === 0 && <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Label</label>}
                  <input
                    type="text" value={inst.label}
                    onChange={(e) => updateInstalment(idx, 'label', e.target.value)}
                    placeholder="e.g. Deposit"
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                  />
                </div>
                <div className="col-span-3">
                  {idx === 0 && <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Amount ({sym})</label>}
                  <input
                    type="number" min="0" step="0.01" value={inst.amount}
                    onChange={(e) => updateInstalment(idx, 'amount', e.target.value)}
                    placeholder="0.00"
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                  />
                </div>
                <div className="col-span-4">
                  {idx === 0 && <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Due Date</label>}
                  <input
                    type="date" value={inst.dueDate}
                    onChange={(e) => updateInstalment(idx, 'dueDate', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  {instalments.length > 1 && (
                    <button type="button" onClick={() => removeInstalment(idx)}
                      className="text-[#D1D5DB] hover:text-dr transition mt-1">✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button" onClick={addInstalment}
            className="text-[13px] text-pb hover:text-navy font-medium"
          >
            + Add instalment
          </button>

          <div>
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Bank Details <span className="text-dr">*</span></label>
            <textarea
              rows={10} value={bankDetails}
              onChange={(e) => setBankDetails(e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-y font-mono"
            />
          </div>

          {error && <p className="text-[12px] text-dr">{error}</p>}

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={() => setShowPlanForm(false)}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-[18px] py-[9px] bg-pb hover:bg-navy text-white text-[13px] font-semibold rounded-[8px] disabled:opacity-60">
              {saving ? 'Generating…' : `Generate ${instalments.length} Invoice${instalments.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      )}

      {/* Regular invoice list */}
      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : regularInvoices.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF]">No invoices yet. Create an instalment plan above.</p>
      ) : (
        <>
          {regularInvoices.length > 1 && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-green-light border border-sg/20 rounded-[8px] px-3 py-2.5 text-center">
                <p className="text-[12px] text-[#065F46] font-medium">Received</p>
                <p className="text-[15px] font-bold text-[#065F46] mt-0.5">{fmt(paidTotal, sym)}</p>
              </div>
              <div className="bg-amber-light border border-ba/25 rounded-[8px] px-3 py-2.5 text-center">
                <p className="text-[12px] text-[#92400E] font-medium">Outstanding</p>
                <p className="text-[15px] font-bold text-[#92400E] mt-0.5">{fmt(unpaidTotal, sym)}</p>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {regularInvoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
          </div>
        </>
      )}

      {/* ── La Liga Match Ticket section ──────────────────────────────────── */}
      {hasMatchTickets && (
        <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{competition} Match Ticket</h3>
            <span className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${
              mtStatus === 'confirmed'    ? 'bg-green-light text-[#065F46]'
              : mtStatus === 'not_possible' ? 'bg-[#FEE2E2] text-[#DC2626]'
              : 'bg-amber-light text-[#92400E]'
            }`}>
              {mtStatus === 'confirmed' ? 'Confirmed' : mtStatus === 'not_possible' ? 'Not Possible' : 'Pending Confirmation'}
            </span>
          </div>

          <div className="space-y-4">
            {/* Status selector */}
            <div>
              <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Fixture Status</label>
              <select
                value={mtStatus}
                onChange={(e) => setMtStatus(e.target.value)}
                disabled={invoiceAlreadyGenerated}
                className="border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="pending">Pending Confirmation</option>
                <option value="confirmed">Confirmed</option>
                <option value="not_possible">Not Possible</option>
              </select>
            </div>

            {/* Confirmed — detail fields */}
            {mtStatus === 'confirmed' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Home Team</label>
                    <select
                      value={mtHomeTeam}
                      onChange={(e) => setMtHomeTeam(e.target.value)}
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="Elche">Elche</option>
                      <option value="Levante">Levante</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Fixture Date</label>
                    <input
                      type="date"
                      value={mtFixtureDate}
                      onChange={(e) => setMtFixtureDate(e.target.value)}
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Kick-off Time</label>
                    <input
                      type="time"
                      value={mtKickOffTime}
                      onChange={(e) => setMtKickOffTime(e.target.value)}
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
                      No. of Tickets
                      <span className="text-[11px] font-normal text-[#9CA3AF] ml-1">
                        (default: {defaultTickets})
                      </span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={mtNumberOfTickets}
                      onChange={(e) => setMtNumberOfTickets(e.target.value)}
                      placeholder={String(defaultTickets)}
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Cost to MST per ticket (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={mtCostToMST}
                      onChange={(e) => setMtCostToMST(e.target.value)}
                      placeholder="0.00"
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Charge to customer per ticket ({sym})</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={mtChargeToCustomer}
                      onChange={(e) => { setMtChargeToCustomer(e.target.value); setMtChargeManuallySet(true); }}
                      placeholder="0.00"
                      disabled={invoiceAlreadyGenerated}
                      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    {!invoiceAlreadyGenerated && mtCostToMST && exchangeRates && (
                      <p className="text-[11px] text-[#9CA3AF] mt-1">
                        {mtChargeManuallySet
                          ? <button type="button" className="underline hover:text-[#6B7280]" onClick={() => setMtChargeManuallySet(false)}>Reset to calculated</button>
                          : `Auto-calculated · ${payingPlayers} paying player${payingPlayers !== 1 ? 's' : ''}`
                        }
                      </p>
                    )}
                  </div>
                </div>

                {/* Total preview */}
                {mtChargeToCustomer && (Number(mtNumberOfTickets) || defaultTickets) > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 bg-[#F9FAFB] rounded-[8px] text-[12px]">
                    <span className="text-[#6B7280]">Invoice total (customer)</span>
                    <span className="font-semibold text-[#111827]">
                      {fmt((Number(mtNumberOfTickets) || defaultTickets) * (Number(mtChargeToCustomer) || 0), sym)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Not possible */}
            {mtStatus === 'not_possible' && (
              <p className="text-[12px] text-[#9CA3AF] bg-[#F9FAFB] rounded-[8px] px-3 py-2">
                No match ticket invoice will be issued. Any customer communication is handled outside the portal.
              </p>
            )}

            {/* Save / Generate Invoice */}
            {!invoiceAlreadyGenerated && (
              <div className="flex items-center gap-3">
                <button
                  onClick={saveMatchTicket}
                  disabled={savingMt}
                  className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
                >
                  {savingMt ? 'Saving…' : 'Save'}
                </button>
                {mtSaved && <span className="text-[13px] text-sg font-medium">Saved</span>}

                {/* Generate Invoice — only available once confirmed details are saved to Firestore */}
                {request?.matchTicket?.status === 'confirmed' && (
                  <button
                    onClick={generateMatchTicketInvoice}
                    disabled={generatingMtInvoice || !request.matchTicket?.fixtureDate || !request.matchTicket?.chargeToCustomerPerTicket}
                    className="px-4 py-2 text-[13px] font-semibold text-white bg-sg hover:bg-[#155e37] rounded-[8px] transition disabled:opacity-60"
                  >
                    {generatingMtInvoice ? 'Generating…' : 'Generate Invoice'}
                  </button>
                )}
              </div>
            )}

            {mtInvoiceError && <p className="text-[12px] text-dr">{mtInvoiceError}</p>}

            {/* Existing match ticket invoice */}
            {matchTicketInvoice && (
              <div className="border border-[#DBEAFE] rounded-[8px] p-4 bg-[#EFF6FF]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[13px] font-semibold text-[#111827]">{matchTicketInvoice.invoiceNumber}</span>
                      <span className="text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] bg-[#DBEAFE] text-[#1D4ED8]">
                        Match Ticket Invoice
                      </span>
                      <span className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${
                        matchTicketInvoice.status === 'paid' ? 'bg-green-light text-[#065F46]' : 'bg-amber-light text-[#92400E]'
                      }`}>
                        {matchTicketInvoice.status === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                    <p className="text-[12px] text-[#9CA3AF]">
                      Due {formatDate(matchTicketInvoice.dueDate)}
                      {matchTicketInvoice.matchTicketDetails?.fixtureDate && ` · Fixture: ${formatDate(matchTicketInvoice.matchTicketDetails.fixtureDate)}`}
                      {matchTicketInvoice.matchTicketDetails?.homeTeam && ` · ${matchTicketInvoice.matchTicketDetails.homeTeam}`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[15px] font-bold text-[#111827]">{fmt(matchTicketInvoice.amount, sym)}</p>
                    <div className="flex items-center justify-end gap-3 mt-1">
                      {!isYourTour && (
                        <button
                          onClick={async () => {
                            setDownloadingId(matchTicketInvoice.id);
                            try { await downloadInvoicePdf(matchTicketInvoice.id, matchTicketInvoice.invoiceNumber); }
                            catch { alert('Failed to download PDF. Please try again.'); }
                            finally { setDownloadingId(null); }
                          }}
                          disabled={downloadingId === matchTicketInvoice.id}
                          className="text-[12px] text-pb hover:text-navy font-medium disabled:opacity-50"
                        >
                          {downloadingId === matchTicketInvoice.id ? 'Generating…' : 'PDF'}
                        </button>
                      )}
                      {!isYourTour && (matchTicketInvoice.status === 'unpaid' ? (
                        <button onClick={() => markPaid(matchTicketInvoice.id)}
                          className="text-[12px] text-sg hover:text-[#155e37] font-medium">
                          Mark as paid
                        </button>
                      ) : (
                        <button onClick={() => markUnpaid(matchTicketInvoice.id)}
                          className="text-[12px] text-[#9CA3AF] hover:text-[#6B7280]">
                          Mark as unpaid
                        </button>
                      ))}
                      <button
                        onClick={() => deleteInvoice(matchTicketInvoice.id, matchTicketInvoice.invoiceNumber)}
                        className="text-[12px] text-dr/60 hover:text-dr font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
